import { GAME_CONFIG, type GameConfig } from './config';
import { RARITIES, type Rarity } from './rarity';

export interface CombatStats {
  attack: number;
  defense: number;
}

export type CombatRole = 'attacker' | 'tank' | 'support';
export const COMBAT_ROLES: readonly CombatRole[] = ['attacker', 'tank', 'support'];

export interface CombatStatsInput {
  id: number;
  rarity: Rarity;
  name: string;
  animeTitle: string | null;
  
  favourites: number;
  
  animePopularity: number;
}

const NOISE_MULTIPLIER = 2654435761;


function letters(text: string | null): number {
  return text ? [...text.split(' ').join('')].length : 0;
}


function idNoise(id: number): number {
  return Number((BigInt(Math.trunc(id)) * BigInt(NOISE_MULTIPLIER)) & 0xffffffffn) / 4294967296;
}


export function computeCombatStats(input: CombatStatsInput, config: GameConfig = GAME_CONFIG): CombatStats {
  const k = config.combat;
  const favourites = Math.max(0, input.favourites);
  const popularity = Math.max(0, input.animePopularity);
  const budget =
    k.rarityBase[input.rarity] *
    2 *
    (1 + k.fameWeight * (favourites / (favourites + k.fameHalf))) *
    (1 + k.hypeWeight * (popularity / (popularity + k.hypeHalf)));
  const x = (letters(input.name) - letters(input.animeTitle) / 2 - k.nameLeanOffset) / k.nameLeanScale;
  const share = Math.min(
    k.maxAttackShare,
    Math.max(k.minAttackShare, 0.5 + k.nameLean * (x / (1 + Math.abs(x))) + (idNoise(input.id) - 0.5) * k.jitter),
  );
  const attack = Math.max(1, Math.round(budget * share));
  const defense = Math.max(1, Math.round(budget) - attack);
  return { attack, defense };
}

function levelFactor(upgradeLevel: number, config: GameConfig): number {
  return 1 + config.combat.levelStep * (Math.max(1, Math.floor(upgradeLevel)) - 1);
}


export function scaleCombatStats(base: CombatStats, upgradeLevel: number, config: GameConfig = GAME_CONFIG): CombatStats {
  const factor = levelFactor(upgradeLevel, config);
  return { attack: Math.round(base.attack * factor), defense: Math.round(base.defense * factor) };
}


export function applyCombatRole(stats: CombatStats, role: CombatRole, config: GameConfig = GAME_CONFIG): CombatStats {
  const modifier = config.combat.roleModifiers[role];
  return {
    attack: Math.max(1, Math.round(stats.attack * modifier.attack)),
    defense: Math.max(1, Math.round(stats.defense * modifier.defense)),
  };
}

function sqlNumber(value: number): string {
  return Number.isInteger(value) ? `${value}.0` : String(value);
}


export function combatSql(
  options: { character?: string; anime?: string; level?: string } = {},
  config: GameConfig = GAME_CONFIG,
): { attack: string; defense: string } {
  const k = config.combat;
  const c = options.character ?? 'c';
  const a = options.anime ?? 'a';
  const base = `(CASE ${c}.rarity ${RARITIES.map((rarity) => `WHEN '${rarity}' THEN ${k.rarityBase[rarity]}`).join(' ')} ELSE ${k.rarityBase.common} END)`;
  const favourites = `MAX(0, ${c}.favourites * 1.0)`;
  const popularity = `MAX(0, COALESCE(${a}.popularity, 0) * 1.0)`;
  const budget =
    `(${base} * 2 * (1 + ${sqlNumber(k.fameWeight)} * (${favourites} / (${favourites} + ${sqlNumber(k.fameHalf)})))` +
    ` * (1 + ${sqlNumber(k.hypeWeight)} * (${popularity} / (${popularity} + ${sqlNumber(k.hypeHalf)}))))`;
  const name = `LENGTH(REPLACE(${c}.name, ' ', ''))`;
  const title = `LENGTH(REPLACE(COALESCE(${a}.title, ''), ' ', ''))`;
  const x = `((${name} - ${title} / 2.0 - ${sqlNumber(k.nameLeanOffset)}) / ${sqlNumber(k.nameLeanScale)})`;
  const noise = `(((${c}.id * ${NOISE_MULTIPLIER}) & 4294967295) / 4294967296.0)`;
  const share =
    `MIN(${sqlNumber(k.maxAttackShare)}, MAX(${sqlNumber(k.minAttackShare)}, 0.5 + ${sqlNumber(k.nameLean)} * (${x} / (1 + ABS(${x})))` +
    ` + (${noise} - 0.5) * ${sqlNumber(k.jitter)}))`;
  const attack = `MAX(1, ROUND(${budget} * ${share}))`;
  const defense = `MAX(1, ROUND(${budget}) - ${attack})`;
  if (!options.level) return { attack, defense };
  const factor = `(1 + ${sqlNumber(k.levelStep)} * (MAX(1, COALESCE(${options.level}, 1)) - 1))`;
  return { attack: `ROUND(${attack} * ${factor})`, defense: `ROUND(${defense} * ${factor})` };
}
